import {
  DetailedHTMLProps,
  HTMLAttributes,
  useCallback,
  useEffect,
  useState,
} from "react";
import { createUseStyles } from "$utils/createUseStyles";
import { useDropzone } from "react-dropzone";
import { parse } from "csv-parse/browser/esm";
import * as XLSX from "xlsx";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import { Text } from "$components";

import { publicIconPath } from "$utils/constants";
import { theme } from "$utils/theme/bataillog";

const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }
  return btoa(binary);
};

const error = {
  IMPORT:
    "Une erreur est survenue lorsque vous avez importé votre fichier. Veuillez importer un nouveau fichier",
  FORMAT:
    "Le fichier que vous essayez d'importer ne correspond pas au format attendu. Veuillez importer un nouveau fichier",
};
type Props = {
  onImportSuccess: (data: any, file: any) => void;
  onImportError: (err: string) => void;
  succes?: boolean;
  mainErrorMessage?: string;
  ErrorMsgOverride?: string;
  importType?: string;
};

function ImportFile({
  onImportSuccess,
  onImportError,
  succes: _succes,
  mainErrorMessage = "",
  ErrorMsgOverride,
  importType = "",
  ...anyProps
}: Props & DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>) {
  const classes = useStyles();
  const [importSucces, setImportSucces] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [mainError, setMainError] = useState<any>(mainErrorMessage || "");

  // Excel often expands the sheet range with blank rows; those must not fail validation.
  const isEmptyRow = useCallback((row: any[] | undefined) => {
    if (!row || row.length === 0) return true;
    return row.every(
      (cell) => cell === "" || cell === null || cell === undefined
    );
  }, []);

  const isPositiveIntQty = useCallback((value: unknown) => {
    const asString = String(value ?? "").trim();
    return /^\d+$/.test(asString) && Number(asString) >= 0;
  }, []);

  const sanitizeImportRows = useCallback(
    (dataTemp: any[][]) => {
      if (!dataTemp?.length) return dataTemp;
      const [header, ...body] = dataTemp;
      return [header, ...body.filter((row) => !isEmptyRow(row))];
    },
    [isEmptyRow]
  );

  const validateRows = useCallback(
    (dataTemp: any[][]) => {
      if (importType === "hc") {
        dataTemp.forEach((d: any, i: number) => {
          if (i === 0 || isEmptyRow(d)) return;
          if (!isPositiveIntQty(d[4]) || +String(d[4]).trim() < 1) {
            throw new Error("La quantité doit être rempli");
          }
        });
      } else if (importType === "panier") {
        dataTemp.forEach((d: any, i: number) => {
          if (i === 0 || isEmptyRow(d)) return;
          // Accept Excel numeric cells (number) and digit strings; reject blanks on filled rows.
          if (!isPositiveIntQty(d[1])) {
            throw new Error("La quantité doit être rempli");
          }
        });
      }
    },
    [importType, isEmptyRow, isPositiveIntQty]
  );

  const onDrop = useCallback(
    (acceptedFiles: any) => {
      try {
        if (!acceptedFiles || acceptedFiles.length === 0) return;
        const file = acceptedFiles[0];
        const isXlsx = /\.(xlsx|xls)$/i.test(file.name);

        const reader = new FileReader();

        reader.onabort = () => {
          console.error("FileReader aborted");
          setErrorMessage(ErrorMsgOverride || error.IMPORT);
        };
        reader.onerror = () => {
          console.error("FileReader error");
          setErrorMessage(ErrorMsgOverride || error.IMPORT);
        };

        if (isXlsx) {
          reader.onload = () => {
            try {
              const data = new Uint8Array(reader.result as ArrayBuffer);
              const workbook = XLSX.read(data, { type: "array" });
              const sheet = workbook.Sheets[workbook.SheetNames[0]];
              const dataTemp: any[][] = XLSX.utils.sheet_to_json(sheet, {
                header: 1,
                defval: "",
              });

              if (!dataTemp || dataTemp.length === 0) {
                setErrorMessage(
                  ErrorMsgOverride ||
                    "Aucune donnée valide trouvée dans le fichier"
                );
                return;
              }

              try {
                const cleaned = sanitizeImportRows(dataTemp);
                if (!cleaned || cleaned.length <= 1) {
                  setErrorMessage(
                    ErrorMsgOverride ||
                      "Aucune donnée valide trouvée dans le fichier"
                  );
                  return;
                }
                validateRows(cleaned);
                setImportSucces(true);
                setErrorMessage("");
                onImportSuccess(cleaned, {
                  result: arrayBufferToBase64(reader.result as ArrayBuffer),
                  file,
                  encoding: "base64",
                });
              } catch (importErr: any) {
                console.error(importErr);
                setErrorMessage(ErrorMsgOverride || error.IMPORT);
              }
            } catch (e: any) {
              console.error("XLSX parsing error:", e);
              setErrorMessage(ErrorMsgOverride || error.IMPORT);
            }
          };
          reader.readAsArrayBuffer(file);
        } else {
          reader.onload = () => {
            try {
              if (!reader) {
                console.error("Reader is null");
                setErrorMessage(ErrorMsgOverride || error.IMPORT);
                return;
              }
              const result = (reader?.result as string)
                .split("\n")
                .shift() as string;

              let delimiter: string;
              const hasSemicolon = result.indexOf(";") >= 0;
              const hasComa = result.indexOf(",") >= 0;
              const hasTab = result.indexOf("\t") >= 0;

              if (hasSemicolon) {
                delimiter = ";";
              } else if (hasComa) {
                delimiter = ",";
              } else if (hasTab) {
                delimiter = "\t";
              } else {
                console.error("No delimiter found in file content:", result);
                throw new Error(
                  "Aucun délimiteur trouvé pour ce fichier CSV ..."
                );
              }

              parse(
                (reader.result as any).replace(/^/, ""),
                {
                  quote: '"',
                  ltrim: true,
                  rtrim: true,
                  bom: true,
                  delimiter,
                  skip_empty_lines: true,
                },
                (err: any, dataTemp: any) => {
                  if (err) {
                    console.error("CSV parsing error:", err);

                    let errorMsg = "Erreur lors de l'analyse du fichier CSV";

                    if (
                      err.message &&
                      err.message.includes("Invalid Record Length")
                    ) {
                      const match = err.message.match(
                        /expect (\d+), got (\d+) on line (\d+)/
                      );
                      if (match) {
                        const [, expected, actual, lineNumber] = match;
                        errorMsg = `Erreur de format CSV : La ligne ${lineNumber} contient ${actual} colonnes au lieu de ${expected}. Vérifiez que toutes les lignes ont le même nombre de colonnes que l'en-tête.`;
                      } else {
                        errorMsg =
                          "Erreur de format CSV : Le nombre de colonnes n'est pas cohérent dans le fichier. Vérifiez que toutes les lignes ont le même nombre de colonnes que l'en-tête.";
                      }
                    } else if (err.message) {
                      errorMsg = `Erreur lors de l'analyse du fichier CSV : ${err.message}`;
                    }

                    setErrorMessage(ErrorMsgOverride || errorMsg);
                    return;
                  }
                  if (dataTemp && dataTemp.length > 0) {
                    try {
                      const cleaned = sanitizeImportRows(dataTemp);
                      if (!cleaned || cleaned.length <= 1) {
                        setErrorMessage(
                          ErrorMsgOverride ||
                            "Aucune donnée valide trouvée dans le fichier CSV"
                        );
                        return;
                      }
                      validateRows(cleaned);
                      setImportSucces(true);
                      setErrorMessage("");
                      onImportSuccess(cleaned, {
                        result: reader.result,
                        file,
                        encoding: "utf8",
                      });
                    } catch (importErr: any) {
                      console.error(importErr);
                      setErrorMessage(ErrorMsgOverride || error.IMPORT);
                    }
                  } else {
                    console.error("dataTemp is undefined or empty");
                    setErrorMessage(
                      ErrorMsgOverride ||
                        "Aucune donnée valide trouvée dans le fichier CSV"
                    );
                  }
                }
              );
            } catch (delimiterErr: any) {
              console.error("Delimiter detection error:", delimiterErr);
              setErrorMessage(ErrorMsgOverride || error.IMPORT);
            }
          };
          reader.readAsText(file, "utf-8");
        }
      } catch (err: any) {
        console.error("File reading error:", err);
        setErrorMessage(ErrorMsgOverride || error.IMPORT);
      }
    },
    [ErrorMsgOverride, onImportSuccess, sanitizeImportRows, validateRows]
  );

  const { getRootProps, getInputProps, fileRejections } = useDropzone({
    onDrop,
    multiple: false,
    accept: {
      "text/csv": [".csv"],
      "application/vnd.ms-excel": [".xls"],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx",
      ],
    },
  });

  useEffect(() => {
    if (fileRejections.length > 0) {
      setErrorMessage(ErrorMsgOverride || error.FORMAT);
    }
  }, [ErrorMsgOverride, fileRejections]);

  useEffect(() => {
    if (errorMessage !== "") {
      onImportError(ErrorMsgOverride || errorMessage);
    }
  }, [ErrorMsgOverride, errorMessage, onImportError]);
  useEffect(() => {
    if (mainErrorMessage !== "") {
      setMainError(mainErrorMessage);
      setImportSucces(false);
    }
  }, [mainErrorMessage]);
  useEffect(() => {
    if (importSucces) {
      setMainError("");
    }
  }, [importSucces]);

  return (
    <div {...anyProps}>
      {!importSucces ? (
        <div className={classes.dragContainer}>
          <div {...getRootProps({ className: classes.dropZone })}>
            <input {...getInputProps()} />
            <div>
              <img
                alt="ico-download"
                src={`${publicIconPath}/download.svg`}
                className={classes.imgUpload}
              />
              <Text className={classes.textMain}>Déposez vos fichiers</Text>
            </div>
            <Text className={classes.textAdd}>
              Ajoutez vos fichiers XLSX ou CSV en les déposant dans cette
              fenêtre
            </Text>
            <Text type="link" className={classes.textLink}>
              Ou parcourez vos documents
            </Text>
          </div>
        </div>
      ) : (
        <div className={classes.importSucces}>
          <CheckCircleIcon className={classes.iconCHeck} />
          Fichier importé avec succès !
        </div>
      )}
      {errorMessage && !mainError && (
        <div className={classes.importError}>
          <ErrorIcon className={classes.iconCHeck} />
          {errorMessage}
        </div>
      )}
      {mainError !== "" && (
        <div className={classes.importError}>
          <ErrorIcon className={classes.iconCHeck} />
          {mainError}
        </div>
      )}
    </div>
  );
}

const useStyles = createUseStyles(() => ({
  dragContainer: {
    border: `1px dashed ${theme.color.gray5}`,
    flex: 1,
    padding: [20, 5],
    cursor: "pointer",
  },
  dropZone: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  textMain: {
    fontSize: 18,
  },
  imgUpload: {
    height: 20,
    marginRight: 10,
  },
  textAdd: {
    marginTop: 12,
  },
  textLink: {
    marginTop: 23,
    color: theme.color.gray3,
    textDecoration: "underline",
  },
  importSucces: {
    display: "flex",
    alignItems: "center",
    color: theme.color.green,
  },
  importError: {
    composes: "$importSucces",
    color: theme.color.red,
    marginTop: 10,
    whiteSpace: "pre-wrap",
  },
  iconCHeck: {
    marginRight: 7,
  },
}));

export default ImportFile;
